import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AddUpdateModal, Filter, Header } from "./component";
import { DataTable, DeleteModal, Excel } from "CommonComponent";
import { Link, useLocation } from "react-router-dom";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

import {
  fetchPrograms,
  deleteProgram,
  getProgramById,
  exportPrograms,
} from "pages/AdminManage/actions";
import AccessRights from "utils/AccessRights";
import "./Programs.css";

const Programs = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const programState = useSelector((s) => s.programState);
  const session = useSelector((s) => s.session);

  const object_viewed_id = session?.current_page_permissions?.object_id || "";

  const [showAddUpdateModal, setShowAddUpdateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [extraFiltersState, setExtraFiltersState] = useState({
    values: {},
  });

  useEffect(() => {
    if (
      session.current_page_permissions &&
      session.current_page_permissions.page_url === location.pathname
    ) {
      dispatch(
        fetchPrograms({ ...extraFiltersState.values, object_viewed_id }),
      );
    }
  }, [session.current_page_permissions]);

  useEffect(() => {
    if (
      programState.redirectToList &&
      session.current_page_permissions.page_url === location.pathname
    ) {
      dispatch(
        fetchPrograms({ ...extraFiltersState.values, object_viewed_id }),
      );
    }
  }, [programState.redirectToList, session.current_page_permissions]);

  // Removed delayed modal opening — handled directly in handleEdit
  useEffect(() => {
    if (!programState.showUpdateForm) {
      setIsEdit(false);
      setShowAddUpdateModal(false);
    }
  }, [programState.showUpdateForm]);

  const handleAdd = () => {
    setIsEdit(false);
    setShowAddUpdateModal(true);
  };

  const handleEdit = (row) => {
    setSelectedRow(row);
    setIsEdit(true);
    setShowAddUpdateModal(true); // Open immediately
    dispatch(getProgramById(row.id, "update", object_viewed_id)); // Fetch data
  };

  const handleDelete = (row) => {
    setSelectedRow(row);
    setShowDeleteModal(true);
  };

  const confirmDelete = () => {
    if (!selectedRow) return; // small safety guard
    dispatch(deleteProgram(selectedRow.id, object_viewed_id));
    setShowDeleteModal(false);
  };

  const getActions = (row) => (
    <div
      className="actionClass"
      style={{ textAlign: "center", whiteSpace: "nowrap" }}
    >
      {AccessRights(session.current_page_permissions, "edit", row.created_by) &&
        row.is_deleted === "n" && (
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip id={`tooltip-edit-${row.id}`}>Edit</Tooltip>}
          >
            <img
              src="/assets/images/edit-icon.svg"
              alt="Edit"
              onClick={() => handleEdit(row)}
              style={{ cursor: "pointer", marginRight: "10px" }}
            />
          </OverlayTrigger>
        )}

      {AccessRights(
        session.current_page_permissions,
        "delete",
        row.created_by,
      ) &&
        row.is_deleted === "n" && (
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip id={`tooltip-delete-${row.id}`}>Delete</Tooltip>}
          >
            <img
              src="/assets/images/delete-icon.svg"
              alt="Delete"
              onClick={() => handleDelete(row)}
              style={{ cursor: "pointer" }}
            />
          </OverlayTrigger>
        )}
    </div>
  );

  const columns = useMemo(
    () => [
      {
        id: "program_name",
        header: "Program Name",
        accessor: (row) => row.program_name,
        sortable: true,
        searchable: true,
      },
      {
        id: "manager",
        header: "Manager",
        accessor: (row) => row.manager?.name || "-",
        sortable: true,
        searchable: true,
      },
      {
        id: "tracking",
        header: "Tracking",
        accessor: (row) => (row.tracking === 1 ? "Yes" : "No"),
        sortable: true,
        searchable: true,
      },
      {
        id: "status",
        header: "Status",
        accessor: (row) => (row.is_active === "y" ? "Active" : "Inactive"),
        sortable: true,
        searchable: true,
      },
      {
        id: "actions",
        header: "Actions",
        accessor: null,
        cell: (_, row) => getActions(row),
        sortable: false,
        searchable: false,
      },
    ],
    [session.current_page_permissions],
  );

  const rows = programState.programsList || [];

  const filterTable = () => {
    dispatch(fetchPrograms({ ...extraFiltersState.values, object_viewed_id }));
  };

  const handleExport = () => {
    dispatch(exportPrograms(extraFiltersState.values, object_viewed_id));
  };

  return (
    <div className="wrapper-div">
      <Header
        onAdd={handleAdd}
        extraFilters={extraFiltersState.values}
        object_viewed_id={object_viewed_id}
      />

      <div className="d-flex justify-content-end align-items-center pb-4 px-4 position-sticky top-70 bg-white z-1">
        <div className="d-flex align-items-center gap-3">
          <Excel handleExport={handleExport} />
        </div>
      </div>

      <div>
        <Filter
          onFilter={filterTable}
          extraFiltersState={extraFiltersState}
          setExtraFiltersState={setExtraFiltersState}
        />
        <DataTable
          columns={columns}
          data={rows}
          serverSide={false}
          showSearch={false}
          className="table-container"
        />
      </div>

      {showAddUpdateModal && (
        <AddUpdateModal
          show={showAddUpdateModal}
          handleClose={() => setShowAddUpdateModal(false)}
          isEdit={isEdit}
          object_viewed_id={object_viewed_id}
        />
      )}

      {showDeleteModal && (
        <DeleteModal
          show={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onDelete={confirmDelete}
          message="Are you sure you want to delete this program?"
        />
      )}
    </div>
  );
};

export default Programs;
